import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createChart, CrosshairMode } from 'lightweight-charts'
import dayjs from 'dayjs'
import { formattedNum } from '../../utils'
import { usePrevious } from 'react-use'
import { useDarkModeManager } from '../../contexts/LocalStorage'

const CandleStickChart = ({
  data,
  width,
  height = 300,
  base,
  margin = true,
  valueFormatter = (val) => formattedNum(val, true),
}) => {
  // Reference for DOM element to create chart
  const ref = useRef()
  const [chartCreated, setChartCreated] = useState(null)

  // Theme and color management
  const [darkMode] = useDarkModeManager()
  const textColor = darkMode ? 'white' : 'black'

  // Track previous values for change detection
  const dataPrev = usePrevious(data)
  const previousTheme = usePrevious(darkMode)

  // Format and prepare candlestick data
  const formattedData = React.useMemo(() => {
    if (!data || data.length === 0) return []

    const formatted = data.map((entry) => ({
      time: parseFloat(entry.timestamp),
      open: parseFloat(entry.open),
      low: parseFloat(entry.open),
      close: parseFloat(entry.close),
      high: parseFloat(entry.close),
    }))

    // Add current price point if base is provided
    if (base && formatted.length > 0) {
      const lastCandle = formatted[formatted.length - 1]
      const currentPrice = parseFloat(base)
      const lastClose = parseFloat(lastCandle.close)

      formatted.push({
        time: dayjs().unix(),
        open: lastClose,
        close: currentPrice,
        low: Math.min(currentPrice, lastClose),
        high: Math.max(currentPrice, lastClose),
      })
    }

    return formatted
  }, [data, base])

  // Cleanup function to properly remove chart and tooltip
  const cleanupChart = useCallback(() => {
    if (!chartCreated) return

    try {
      const tooltip = document.getElementById('tooltip-id')
      const container = document.getElementById('test-id')

      if (tooltip && container && container.contains(tooltip)) {
        container.removeChild(tooltip)
      }

      if (chartCreated && typeof chartCreated.remove === 'function') {
        chartCreated.remove()
      }

      setChartCreated(null)
    } catch (error) {
      console.warn('Error cleaning up chart:', error)
      setChartCreated(null)
    }
  }, [chartCreated])

  // Reset chart when theme changes
  useEffect(() => {
    if (chartCreated && previousTheme !== undefined && previousTheme !== darkMode) {
      cleanupChart()
    }
  }, [chartCreated, darkMode, previousTheme, cleanupChart])

  // Reset chart when data changes
  useEffect(() => {
    if (chartCreated && dataPrev && data !== dataPrev) {
      cleanupChart()
    }
  }, [chartCreated, data, dataPrev, cleanupChart])

  // Create chart with proper configuration
  useEffect(() => {
    if (chartCreated || !ref.current || !formattedData || formattedData.length === 0) {
      return
    }

    try {
      // Create chart with responsive configuration
      const chart = createChart(ref.current, {
        width: width,
        height: height,
        layout: {
          backgroundColor: 'transparent',
          textColor: textColor,
          fontFamily: "'Inter Display', sans-serif",
        },
        grid: {
          vertLines: {
            color: 'rgba(197, 203, 206, 0.5)',
          },
          horzLines: {
            color: 'rgba(197, 203, 206, 0.5)',
          },
        },
        crosshair: {
          mode: CrosshairMode.Normal,
        },
        rightPriceScale: {
          borderColor: 'rgba(197, 203, 206, 0.8)',
          visible: true,
        },
        timeScale: {
          borderColor: 'rgba(197, 203, 206, 0.8)',
        },
        localization: {
          priceFormatter: (val) => formattedNum(val),
        },
      })

      // Add candlestick series with clean colors
      const candleSeries = chart.addCandlestickSeries({
        upColor: '#45e560',
        downColor: '#ff8080',
        borderDownColor: '#ff8080',
        borderUpColor: '#45e560',
        wickDownColor: '#ff8080',
        wickUpColor: '#45e560',
      })

      candleSeries.setData(formattedData)

      // Create and configure tooltip element
      const toolTip = document.createElement('div')
      toolTip.setAttribute('id', 'tooltip-id')
      toolTip.className = 'three-line-legend'

      // Apply tooltip styles
      Object.assign(toolTip.style, {
        display: 'block',
        position: 'absolute',
        left: `${margin ? 116 : 10}px`,
        top: '50px',
        backgroundColor: 'transparent',
        pointerEvents: 'none',
        zIndex: 10,
      })

      ref.current.appendChild(toolTip)

      // Helper function to render price tooltip with clean formatting
      const renderPriceTooltip = (price, timestamp = null) => {
        // Validate price value
        if (price === null || price === undefined || isNaN(price)) {
          return ''
        }

        const formattedPrice = valueFormatter(price)

        const priceElement = `
          <div style="
            font-size: 22px;
            font-weight: 600;
            margin: 0;
            padding: 0;
            color: ${textColor};
            line-height: 1.3;
            font-family: 'Inter Display', sans-serif;
            white-space: nowrap;
          ">
            ${formattedPrice}
          </div>
        `

        const timestampElement = timestamp
          ? `
          <div style="
            font-size: 12px;
            font-weight: 400;
            margin: 4px 0 0 0;
            padding: 0;
            color: ${textColor};
            opacity: 0.6;
            line-height: 1.3;
            font-family: 'Inter Display', sans-serif;
            white-space: nowrap;
          ">
            ${dayjs.unix(timestamp).format('MM/DD h:mm A')} UTC
          </div>
        `
          : ''

        return `<div style="display: flex; flex-direction: column; gap: 0;">${priceElement}${timestampElement}</div>`
      }

      // Set initial tooltip to show current base price
      const setDefaultTooltip = () => {
        toolTip.innerHTML = base ? renderPriceTooltip(base) : ''
      }
      setDefaultTooltip()

      // Update tooltip on crosshair move (hover)
      chart.subscribeCrosshairMove(function (param) {
        const isOutOfBounds =
          !param ||
          !param.time ||
          param.point.x < 0 ||
          param.point.x > width ||
          param.point.y < 0 ||
          param.point.y > height

        if (isOutOfBounds) {
          setDefaultTooltip()
        } else {
          const price = param.seriesPrices.get(candleSeries).close
          toolTip.innerHTML = renderPriceTooltip(price, param.time)
        }
      })

      chart.timeScale().fitContent()

      setChartCreated(chart)
    } catch (error) {
      console.error('Error creating candlestick chart:', error)
      setChartCreated(null)
    }
  }, [chartCreated, formattedData, width, height, valueFormatter, base, margin, textColor])

  // Handle chart responsiveness on resize
  useEffect(() => {
    if (!chartCreated || !width || !height) return

    try {
      chartCreated.resize(width, height)
      chartCreated.timeScale().scrollToPosition(0)
    } catch (error) {
      console.warn('Error resizing chart:', error)
    }
  }, [chartCreated, height, width])

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (chartCreated) {
        cleanupChart()
      }
    }
  }, [chartCreated, cleanupChart])

  return (
    <div>
      <div ref={ref} id="test-id" />
    </div>
  )
}

export default CandleStickChart
